import { useMemo, useState } from "react";
import { Landmark } from "lucide-react";
import "./EmiCalculator.css";

const formatINR = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Math.round(n));

export default function EmiCalculator() {
  const [principal, setPrincipal] = useState(2500000);
  const [rate, setRate] = useState(8.5);
  const [years, setYears] = useState(20);

  const { emi, total, interest } = useMemo(() => {
    const r = rate / 12 / 100;
    const n = years * 12;
    let e = 0;
    if (r === 0) e = principal / n;
    else e = (principal * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    const tot = e * n;
    return { emi: e, total: tot, interest: tot - principal };
  }, [principal, rate, years]);

  return (
    <section className="calc-card" id="emi">
      <div className="calc-card__head">
        <div className="calc-card__icon">
          <Landmark size={22} />
        </div>
        <div>
          <h2>EMI Calculator</h2>
          <p>Calculate monthly EMI, total interest, and overall outflow.</p>
        </div>
      </div>

      <div className="calc-layout">
        <div>
          <div className="calc-field">
            <div className="calc-field__top">
              <label htmlFor="emi-p">Loan amount</label>
              <input
                id="emi-p"
                className="calc-field__input"
                type="number"
                min={10000}
                max={100000000}
                step={10000}
                value={principal}
                onChange={(e) => setPrincipal(Number(e.target.value) || 0)}
              />
            </div>
            <input
              className="calc-range"
              type="range"
              min={10000}
              max={20000000}
              step={10000}
              value={Math.min(principal, 20000000)}
              onChange={(e) => setPrincipal(Number(e.target.value))}
            />
          </div>
          <div className="calc-field">
            <div className="calc-field__top">
              <label htmlFor="emi-r">Interest rate (% p.a.)</label>
              <input
                id="emi-r"
                className="calc-field__input"
                type="number"
                min={1}
                max={24}
                step={0.1}
                value={rate}
                onChange={(e) => setRate(Number(e.target.value) || 0)}
              />
            </div>
            <input
              className="calc-range"
              type="range"
              min={1}
              max={24}
              step={0.1}
              value={rate}
              onChange={(e) => setRate(Number(e.target.value))}
            />
          </div>
          <div className="calc-field">
            <div className="calc-field__top">
              <label htmlFor="emi-y">Tenure (years)</label>
              <input
                id="emi-y"
                className="calc-field__input"
                type="number"
                min={1}
                max={40}
                value={years}
                onChange={(e) => setYears(Number(e.target.value) || 1)}
              />
            </div>
            <input
              className="calc-range"
              type="range"
              min={1}
              max={40}
              value={years}
              onChange={(e) => setYears(Number(e.target.value))}
            />
          </div>
        </div>

        <div className="calc-results">
          <div className="calc-result calc-result--accent">
            <span>Monthly EMI</span>
            <strong>{formatINR(emi)}</strong>
          </div>
          <div className="calc-result">
            <span>Total interest</span>
            <strong>{formatINR(interest)}</strong>
          </div>
          <div className="calc-result">
            <span>Total payment</span>
            <strong>{formatINR(total)}</strong>
          </div>
          <p className="calc-note">
            EMI = P × r × (1+r)ⁿ / ((1+r)ⁿ − 1), r = annual÷12÷100, n = years×12.
          </p>
        </div>
      </div>
    </section>
  );
}
